/* ================================================================
   SnapshotContext — Gestion Formation (CEJ CONCEPT)
   v2 (Étape 4) : Cache automatique + moteur de synchronisation

   Fonctions exposées :
     refresh()          — rafraîchit le cache IndexedDB en tâche de fond
     syncNow()          — pousse les mutations en attente vers le serveur
     syncAndRefresh()   — sync puis refresh (utilisé après retour connexion)
     setShowSyncPanel() — ouvre/ferme le panneau de synchronisation

   Déclenchements automatiques :
     • Chargement initial (cache absent ou > AUTO_REFRESH_MIN)
     • Retour de connexion : sync d'abord, puis refresh
     • Timer périodique (AUTO_REFRESH_MIN minutes)
   ================================================================ */

import React, {
  createContext, useContext, useState, useEffect,
  useCallback, useRef,
} from 'react';
import { runSnapshot, getSnapshotMeta, SNAPSHOT_ENDPOINTS } from '../utils/offlineDB';
import { syncPendingMutations } from '../utils/syncEngine';

const API_BASE         = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';
const AUTO_REFRESH_MIN = 30;

const SnapshotContext = createContext({
  // Cache
  meta         : null,
  isRefreshing : false,
  progress     : { current: 0, total: SNAPSHOT_ENDPOINTS.length, label: '' },
  refresh      : () => {},
  // Sync
  isSyncing    : false,
  syncResults  : null,
  syncProgress : { current: 0, total: 0, mutation: null, phase: 'done' },
  syncNow      : () => {},
  syncAndRefresh: () => {},
  // UI panel
  showSyncPanel   : false,
  setShowSyncPanel: () => {},
});

export function useSnapshot() {
  return useContext(SnapshotContext);
}

export function SnapshotProvider({ children }) {

  // ── État cache ──────────────────────────────────────────────
  const [meta,         setMeta]         = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [progress,     setProgress]     = useState({
    current: 0, total: SNAPSHOT_ENDPOINTS.length, label: '',
  });

  // ── État sync ───────────────────────────────────────────────
  const [isSyncing,    setIsSyncing]    = useState(false);
  const [syncResults,  setSyncResults]  = useState(null);
  const [syncProgress, setSyncProgress] = useState({
    current: 0, total: 0, mutation: null, phase: 'done',
  });

  // ── UI ──────────────────────────────────────────────────────
  const [showSyncPanel, setShowSyncPanel] = useState(false);

  // Refs pour éviter les exécutions en parallèle
  const refreshingRef = useRef(false);
  const syncingRef    = useRef(false);

  // ─────────────────────────────────────────────────────────────
  // refresh() — met à jour le cache IndexedDB depuis le serveur
  // ─────────────────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    if (refreshingRef.current || !navigator.onLine) return;
    refreshingRef.current = true;
    setIsRefreshing(true);
    setProgress({ current: 0, total: SNAPSHOT_ENDPOINTS.length, label: '…' });

    const token = localStorage.getItem('gf_token');
    try {
      const result = await runSnapshot(
        API_BASE, token,
        (current, total, label) => setProgress({ current, total, label })
      );
      setMeta(result);
    } catch (err) {
      console.warn('[Cache] Erreur snapshot :', err);
    } finally {
      refreshingRef.current = false;
      setIsRefreshing(false);
    }
  }, []);

  // ─────────────────────────────────────────────────────────────
  // syncNow() — envoie les mutations en attente vers le serveur
  // ─────────────────────────────────────────────────────────────
  const syncNow = useCallback(async () => {
    if (syncingRef.current || !navigator.onLine) return null;
    syncingRef.current = true;
    setIsSyncing(true);
    setSyncResults(null);
    setSyncProgress({ current: 0, total: 0, mutation: null, phase: 'syncing' });

    const token = localStorage.getItem('gf_token');
    let results = null;
    try {
      results = await syncPendingMutations(token, prog => setSyncProgress(prog));
      setSyncResults(results);
    } catch (err) {
      console.warn('[Sync] Erreur :', err);
    } finally {
      syncingRef.current = false;
      setIsSyncing(false);
    }
    return results;
  }, []);

  // ─────────────────────────────────────────────────────────────
  // syncAndRefresh() — sync puis rafraîchit le cache
  // Utilisé après le retour de connexion et par le bouton manuel
  // ─────────────────────────────────────────────────────────────
  const syncAndRefresh = useCallback(async () => {
    const results = await syncNow();
    // Toujours rafraîchir après une tentative de sync
    await refresh();
    return results;
  }, [syncNow, refresh]);

  // ─────────────────────────────────────────────────────────────
  // Chargement initial
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    getSnapshotMeta()
      .then(existingMeta => {
        setMeta(existingMeta);
        if (!existingMeta) { refresh(); return; }
        const ageMin = (Date.now() - existingMeta.timestamp) / 60_000;
        if (ageMin > AUTO_REFRESH_MIN) refresh();
      })
      .catch(() => refresh());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─────────────────────────────────────────────────────────────
  // Retour de connexion → sync en premier, puis refresh
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let timer = null;
    const handleOnline = () => {
      timer = setTimeout(() => syncAndRefresh(), 2500);
    };
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
      if (timer) clearTimeout(timer);
    };
  }, [syncAndRefresh]);

  // ─────────────────────────────────────────────────────────────
  // Timer périodique
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const id = setInterval(() => {
      if (navigator.onLine) refresh();
    }, AUTO_REFRESH_MIN * 60_000);
    return () => clearInterval(id);
  }, [refresh]);

  return (
    <SnapshotContext.Provider value={{
      meta, isRefreshing, progress, refresh,
      isSyncing, syncResults, syncProgress, syncNow, syncAndRefresh,
      showSyncPanel, setShowSyncPanel,
    }}>
      {children}
    </SnapshotContext.Provider>
  );
}
